import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiClient } from '@/api/client';
import {
  API_PRESCHOOL_EVALUATIONS,
  API_PRESCHOOL_EVALUATIONS_APPROVE,
} from '@/api/endpoints';
import { PRESCHOOL_EVALUATION_QUERY_KEY } from '@/api/queryKeys';
import { useUserContext } from '@/auth/UserContext';
import type { PreschoolEvaluationResponse } from '@/types/preschool';
import { unwrapAxiosResponse } from '@/utils/axios';
import { ErrorMessage, logRequestError } from '@/utils/errors';

interface Params {
  isOpen: boolean;
  onApproveFailure: () => void;
}

export const usePreschoolEvaluation = ({
  isOpen,
  onApproveFailure,
}: Params) => {
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const [hasJustApproved, setHasJustApproved] = useState(false);

  const {
    data: evaluation,
    isLoading,
    isError,
  } = useQuery<PreschoolEvaluationResponse>({
    queryKey: PRESCHOOL_EVALUATION_QUERY_KEY,
    enabled: isOpen,
    staleTime: Infinity,
    queryFn: () =>
      apiClient
        .get<PreschoolEvaluationResponse>(API_PRESCHOOL_EVALUATIONS)
        .then(unwrapAxiosResponse),
  });

  const { mutate: approve, isPending: isApproving } = useMutation({
    mutationFn: () => apiClient.post(API_PRESCHOOL_EVALUATIONS_APPROVE),
    onSuccess: async () => {
      setHasJustApproved(true);

      await queryClient.invalidateQueries({
        queryKey: PRESCHOOL_EVALUATION_QUERY_KEY,
      });
    },
    onError: (error) => {
      logRequestError(ErrorMessage.ApprovePreschoolEvaluation, error);
      onApproveFailure();
    },
  });

  const isApproved =
    evaluation?.parentStatuses?.some(
      (status) => status.personType === user?.personType && status.hasApproved,
    ) ?? false;

  return {
    evaluation,
    isLoading,
    isError,
    isApproved,
    isApproving,
    hasJustApproved,
    approve,
  };
};
